'use client';

import { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Loader2, DollarSign, ArrowRight, Shield, Zap, CheckCircle, Send } from 'lucide-react';
import { trackLeadGeneration, trackEvent } from '@/lib/analytics';
import { trackLeadConversion } from '@/lib/trackConversion';
import { captureUTMParams, getStoredUTMParams, storeUTMParams } from '@/lib/apply/utm';

interface MasterQuoteFormProps {
  page: string;
  onAmountChange?: (amount: number) => void;
  initialAmount?: number;
  triggerContinueAnimation?: boolean;
}

export function MasterQuoteForm({ page, onAmountChange, initialAmount, triggerContinueAnimation }: MasterQuoteFormProps) {
  useEffect(() => {
    const linkCm = document.createElement('link');
    linkCm.rel = 'prefetch';
    linkCm.href = '/cm-thanks';
    document.head.appendChild(linkCm);
    const linkThanks = document.createElement('link');
    linkThanks.rel = 'prefetch';
    linkThanks.href = '/thanks';
    document.head.appendChild(linkThanks);
    return () => {
      document.head.removeChild(linkCm);
      document.head.removeChild(linkThanks);
    };
  }, []);
  // Function to get page context - prioritize pathname over prop for reliability
  const getPageContext = (): string => {
    // First priority: check pathname if on client side (more reliable)
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      
      // Check for specific pages first - using more specific path check
      if (pathname.includes('/self-employed')) {
        return 'self-employed';
      }
      if (pathname.includes('/chattel-mortgage')) {
        return 'chattel-mortgage';
      }
      if (pathname.includes('/car-finance')) {
        return 'car-finance';
      }
      if (pathname.includes('/equipment-finance')) {
        return 'equipment-finance';
      }
      if (pathname.includes('/truck-finance')) {
        return 'truck-finance';
      }
      
      // Only return 'home' if we're actually on the home page
      if (pathname === '/' || pathname === '') {
        return 'home';
      }
      
      // If we have an unrecognized pathname but a valid prop, use the prop
      if (page && page !== 'home') {
        return page;
      }
    }
    
    // Server-side or initial render: use the prop if available and not 'home'
    // Guard against using 'home' when we shouldn't be
    if (page && page !== 'home') {
      return page;
    }
    
    // Only use 'home' as a last resort when we're actually on home or have no other info
    if (typeof window !== 'undefined' && window.location.pathname !== '/' && window.location.pathname !== '') {
      // If we're not on home but somehow got here, prefer the prop
      return page || 'self-employed'; // Default to the context we expect for SSR issues
    }
    
    return page || 'home';
  };
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animateContinue, setAnimateContinue] = useState(false);
  const [formData, setFormData] = useState({
    amount: 0,
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    abnTenure: '',
    consent: false,
    rid: '',
    // UTM params
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    gclid: '',
  });

  // Generate RID on mount and notify initial amount
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    const rid = `app_${timestamp}_${random}`;
    
    // Capture UTM params from URL
    const urlParams = captureUTMParams();
    
    // Get previously stored UTM params as fallback
    const storedParams = getStoredUTMParams();
    
    // Merge: URL params take priority, fall back to stored values
    const mergedParams = {
      utm_source: urlParams.utm_source || storedParams.utm_source || '',
      utm_medium: urlParams.utm_medium || storedParams.utm_medium || '',
      utm_campaign: urlParams.utm_campaign || storedParams.utm_campaign || '',
      utm_term: urlParams.utm_term || storedParams.utm_term || '',
      utm_content: urlParams.utm_content || storedParams.utm_content || '',
      gclid: urlParams.gclid || storedParams.gclid || '',
    };
    
    // Store the merged params for future page loads
    // Only store if we have at least one value (don't overwrite with empty)
    if (Object.values(mergedParams).some(v => v)) {
      storeUTMParams(mergedParams);
    }
    
    setFormData(prev => ({
      ...prev,
      rid,
      ...mergedParams,
    }));
    
    // No initial amount pre-fill
  }, [onAmountChange]);

  useEffect(() => {
    if (initialAmount && initialAmount > 0) {
      setFormData(prev => ({ ...prev, amount: initialAmount }));
      if (onAmountChange) onAmountChange(initialAmount);
    }
  }, [initialAmount, onAmountChange]);

  useEffect(() => {
    if (triggerContinueAnimation && currentStep === 0) {
      setAnimateContinue(true);
    }
    if (!triggerContinueAnimation) {
      setAnimateContinue(false);
    }
  }, [triggerContinueAnimation, currentStep]);

  const steps = ['amount', 'contact', 'confirm'];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleAmountInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const numValue = parseInt(value) || 0;
    if (numValue <= 2000000) {
      setFormData(prev => ({ ...prev, amount: numValue }));
      // Notify parent of amount change
      if (onAmountChange) {
        onAmountChange(numValue);
      }
    }
  };

  const formatAmountDisplay = (value: number) => {
    if (value === 0) return '';
    return value.toLocaleString('en-AU');
  };

  const formatMobile = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.startsWith('61')) {
      const mobile = cleaned.slice(2);
      if (mobile.length <= 3) return `+61 ${mobile}`;
      if (mobile.length <= 6) return `+61 ${mobile.slice(0, 3)} ${mobile.slice(3)}`;
      return `+61 ${mobile.slice(0, 3)} ${mobile.slice(3, 6)} ${mobile.slice(6, 9)}`;
    }
    if (cleaned.startsWith('04')) {
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 7) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 10)}`;
    }
    return value;
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatMobile(e.target.value);
    setFormData(prev => ({ ...prev, mobile: formatted }));
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 0: // amount
        return formData.amount >= 10000 && formData.amount <= 2000000 && formData.amount > 0;
      case 1: // contact
        const mobileClean = formData.mobile.replace(/\D/g, '');
        const validMobile = (mobileClean.startsWith('04') && mobileClean.length === 10) ||
                           (mobileClean.startsWith('614') && mobileClean.length === 11);
        const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
        return formData.firstName && formData.lastName && validMobile && validEmail && formData.abnTenure;
      case 2: // confirm
        return formData.consent;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    setIsSubmitting(true);
    
    // Get the current page context using the prop with fallback
    const currentPage = getPageContext();
    
    try {
      // Track analytics with enhanced GA4 events
      trackLeadGeneration({
        amount: formData.amount,
        abn_tenure: formData.abnTenure === '0-12m' ? '3' : formData.abnTenure === '1-2y' ? '5' : '7',
      });
      
      // Track form submit event for Google Ads
      trackEvent('form_submit', {
        form_id: 'master_quote_form',
        value: formData.amount,
        currency: 'AUD',
        page: currentPage,
      });
      
      // Prepare submission data with proper page context
      const submissionData = {
        ...formData,
        mobile: formData.mobile.replace(/\D/g, ''),
        consent_timestamp: new Date().toISOString(),
        sms_consent: formData.consent,
        page_context: currentPage,
        // Include both amount and loanAmount for server compatibility
        loanAmount: formData.amount,
        // Include name field for server compatibility (combines first and last)
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        // Include phone as alias for mobile (send cleaned number)
        phone: formData.mobile.replace(/\D/g, ''),
      };
      
      // Submit to API
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      });
      
      if (response.ok) {
        // Save lead data to localStorage for apply page pre-fill
        const leadData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          mobile: formData.mobile.replace(/\D/g, ''), // Store clean format
          email: formData.email,
          amount: formData.amount,
          abnTenure: formData.abnTenure,
          rid: formData.rid,
          smsConsent: formData.consent,
          utm_source: formData.utm_source,
          utm_medium: formData.utm_medium,
          utm_campaign: formData.utm_campaign,
          utm_term: formData.utm_term,
          utm_content: formData.utm_content,
          gclid: formData.gclid,
        };
        
        try {
          localStorage.setItem('vgfs_lead_data', JSON.stringify(leadData));
          console.log('[Lead Data] Saved to localStorage for apply page pre-fill');
        } catch (e) {
          console.error('[Lead Data] Failed to save to localStorage:', e);
        }
        
        // Fire GA4 generate_lead event AFTER successful API submission
        // Uses sessionStorage dedupe to fire exactly once per unique rid
        // For truck-finance, conversion fires on /thanks page load only (canonical conversion point)
        if (currentPage !== 'truck-finance') {
          trackLeadConversion({
            rid: formData.rid,
            loan_amount: formData.amount,
            product: currentPage === 'chattel-mortgage' ? 'chattel_mortgage' : currentPage,
            currency: 'AUD',
            value: 1,
          });
        }
        
        // Build redirect URL with SAFE params only (NO PII)
        // PII is already saved in localStorage above - no need in URL
        const params = new URLSearchParams({
          rid: formData.rid,
          amount: formData.amount.toString(),
          arrival_from: 'lead_submit', // Required for Ads one-shot tracking
        });
        
        // Add page_context per Alex's specification
        if (currentPage === 'home') {
          params.append('page_context', 'thanks_home');
        } else if (currentPage === 'chattel-mortgage') {
          params.append('page_context', 'thanks_chattel_mortgage');
        } else if (currentPage === 'self-employed') {
          params.append('page_context', 'thanks_self_employed');
        } else {
          params.append('page_context', `thanks_${currentPage}`);
        }
        
        // Add UTM params if they exist (safe, non-PII)
        if (formData.utm_source) params.append('utm_source', formData.utm_source);
        if (formData.utm_medium) params.append('utm_medium', formData.utm_medium);
        if (formData.utm_campaign) params.append('utm_campaign', formData.utm_campaign);
        if (formData.utm_term) params.append('utm_term', formData.utm_term);
        if (formData.utm_content) params.append('utm_content', formData.utm_content);
        if (formData.gclid) params.append('gclid', formData.gclid);
        
        // Hard redirect per Alex's specification - use /cm-thanks for home, chattel-mortgage, and self-employed, /thanks for others
        const useCmThanks = currentPage === 'home' || currentPage === 'chattel-mortgage' || currentPage === 'self-employed';
        const redirectUrl = useCmThanks ? '/cm-thanks' : '/thanks';
        
        if (typeof window !== 'undefined') {
          window.location.href = `${redirectUrl}?${params.toString()}`;
        }
      } else {
        alert('Something went wrong. Please try again or call 03 8456 9966.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again or call 03 8456 9966.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-8 max-w-lg mx-auto border border-gray-100 relative overflow-hidden">
      <style>{`@keyframes continueGlow{0%{box-shadow:0 0 0 0 rgba(249,115,22,0.7)}50%{box-shadow:0 0 0 14px rgba(249,115,22,0)}100%{box-shadow:0 0 0 0 rgba(249,115,22,0)}}`}</style>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#FF8C42]/10 to-transparent rounded-full blur-2xl" />
      <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-[#1D3557]/5 to-transparent rounded-full blur-2xl" />
      
      {/* Header with engaging title */}
      <div className="relative z-10 text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] text-white text-sm font-semibold px-4 py-2 rounded-full mb-3">
          <Zap className="w-4 h-4" />
          <span>Quick 60-Second Quote</span>
        </div>
        <h2 className="text-2xl font-bold text-[#1D3557] mb-2" style={{ fontFamily: 'var(--font-poppins)' }}>Get Your Finance Approved Fast</h2>
        <p className="text-gray-600 text-sm">Trusted by 2,000+ Australian businesses</p>
      </div>
      {/* Enhanced Step Indicator */}
      <div className="relative mb-8">
        <div className="flex justify-between relative z-10">
          {steps.map((step, index) => (
            <div key={step} className="flex-1 relative">
              <div className="flex items-center">
                <div
                  className={`flex-1 h-3 rounded-full mx-1 transition-all duration-500 ${
                    index <= currentStep 
                      ? 'bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] shadow-lg shadow-[#FF8C42]/30' 
                      : 'bg-gray-200'
                  }`}
                />
                {index === currentStep && (
                  <div className="absolute inset-0 h-3 rounded-full mx-1 bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] animate-pulse opacity-50" />
                )}
              </div>
              <div className="text-xs text-center mt-2 font-medium text-gray-600">
                {index === 0 ? 'Amount' : index === 1 ? 'Details' : 'Confirm'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="mb-8">
        {/* Step 1: Amount - Enhanced with numerical input */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#1D3557] mb-2">How much finance do you need?</h3>
              <p className="text-gray-600 text-sm">Enter any amount between $10,000 and $2,000,000</p>
            </div>
            
            {/* Amount Input with enhanced styling */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity" />
              <div className="relative bg-white rounded-2xl border-2 border-gray-200 hover:border-[#FF8C42] transition-all duration-300 p-4 md:p-6">
                <div className="flex items-center justify-center gap-2 md:gap-3">
                  <DollarSign className="w-7 h-7 md:w-10 md:h-10 text-[#FF8C42] flex-shrink-0" />
                  <input
                    type="text"
                    value={formatAmountDisplay(formData.amount)}
                    onChange={handleAmountInput}
                    onBlur={(e) => {
                      // Ensure amount is updated on blur as well
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      const numValue = parseInt(value) || 0;
                      if (numValue > 0 && numValue <= 2000000 && onAmountChange) {
                        onAmountChange(numValue);
                      }
                    }}
                    placeholder="Enter amount"
                    className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-[#1D3557] w-full outline-none placeholder-gray-400"
                    style={{ fontFamily: 'var(--font-poppins)' }}
                  />
                </div>
              </div>
            </div>
            
            {/* Quick select amounts */}
            <div className="space-y-3">
              <p className="text-xs text-gray-500 text-center font-medium">POPULAR AMOUNTS</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[25000, 50000, 100000, 250000].map(amount => (
                  <button
                    key={amount}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, amount }));
                      // Also call onAmountChange for button clicks
                      if (onAmountChange) {
                        onAmountChange(amount);
                      }
                    }}
                    className={`py-2 sm:py-3 px-2 sm:px-3 rounded-xl border-2 transition-all font-semibold text-xs sm:text-sm ${
                      formData.amount === amount
                        ? 'border-[#FF8C42] bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] text-white shadow-lg shadow-[#FF8C42]/30'
                        : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {formatCurrency(amount).replace('.00', '')}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Trust badges */}
            <div className="flex items-center justify-center gap-6 pt-2">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Zap className="w-4 h-4 text-yellow-600" />
                <span>Instant</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>No obligation</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Contact - Enhanced styling */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#1D3557] mb-2">Almost there! Just a few details</h3>
              <p className="text-gray-600 text-sm">We'll send your quote instantly via SMS</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="First Name *"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-[#FF8C42] focus:outline-none transition-all text-gray-900 placeholder-gray-500 bg-white"
                />
                <input
                  type="text"
                  placeholder="Last Name *"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-[#FF8C42] focus:outline-none transition-all text-gray-900 placeholder-gray-500 bg-white"
                />
              </div>
              <input
                type="tel"
                placeholder="Mobile (04XX XXX XXX) *"
                value={formData.mobile}
                onChange={handleMobileChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-[#FF8C42] focus:outline-none transition-all text-gray-900 placeholder-gray-500 bg-white"
              />
              <input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 focus:border-[#FF8C42] focus:outline-none transition-all text-gray-900 placeholder-gray-500 bg-white"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How long have you had your ABN? *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: '0-12m', label: '0-12 months' },
                    { value: '1-2y', label: '1-2 years' },
                    { value: '2+y', label: '2+ years' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFormData(prev => ({ ...prev, abnTenure: option.value }))}
                      className={`py-3 px-4 rounded-xl border-2 transition-all font-medium ${
                        formData.abnTenure === option.value
                          ? 'border-[#FF8C42] bg-gradient-to-r from-[#FF8C42]/10 to-[#E67C3A]/10 text-[#FF8C42] shadow-md'
                          : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirm - Enhanced review */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#1D3557] mb-2">Perfect! Let's review</h3>
              <p className="text-gray-600 text-sm">Your quote will be sent instantly via SMS</p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-5 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Finance Amount:</span>
                <span className="font-semibold text-gray-900">{formatCurrency(formData.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-semibold text-gray-900">{formData.firstName} {formData.lastName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Mobile:</span>
                <span className="font-semibold text-gray-900">{formData.mobile}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-semibold text-gray-900">{formData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">ABN Status:</span>
                <span className="font-semibold text-gray-900">
                  {formData.abnTenure === '0-12m' ? '0-12 months' :
                   formData.abnTenure === '1-2y' ? '1-2 years' : '2+ years'}
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-[#FF8C42] transition-all cursor-pointer bg-white">
                <input
                  type="checkbox"
                  checked={formData.consent}
                  onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
                  className="mt-1 w-5 h-5 text-[#FF8C42] rounded focus:ring-[#FF8C42]"
                />
                <span className="text-sm text-gray-600">
                  I agree to the Privacy Policy and Terms. I understand that approvals are subject to lender approval and figures are indicative only.
                </span>
              </label>
              
              {/* Trust indicators */}
              <div className="text-center pt-2">
                <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Your information is 100% secure</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Navigation */}
      <div id="continue-btn-row" className="flex justify-between relative z-10">
        {currentStep > 0 && (
          <button
            onClick={handlePrev}
            className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-800 transition-all hover:bg-gray-100 rounded-xl"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>
        )}
        
        <div className="ml-auto">
          {currentStep < 2 ? (
            <button
              onClick={handleNext}
              disabled={!validateStep(currentStep)}
              onAnimationEnd={() => setAnimateContinue(false)}
              style={animateContinue && validateStep(currentStep) ? { animation: 'continueGlow 0.9s ease-out 3' } : {}}
              className={`group flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                validateStep(currentStep)
                  ? 'bg-gradient-to-r from-[#1D3557] to-[#2A4D7A] text-white shadow-lg shadow-[#1D3557]/30 hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className="text-lg">Continue</span>
              <ArrowRight className={`w-5 h-5 transition-transform ${
                validateStep(currentStep) ? 'group-hover:translate-x-1' : ''
              }`} />
            </button>
          ) : (
            <>
            <div className="relative">
              {/* Pulsing background effect for CTA */}
              {validateStep(2) && !isSubmitting && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] rounded-xl blur-xl opacity-60 animate-pulse" />
              )}
              <button
                onClick={handleSubmit}
                disabled={!validateStep(2) || isSubmitting}
                className={`relative px-10 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${
                  validateStep(2) && !isSubmitting
                    ? 'bg-gradient-to-r from-[#FF8C42] to-[#E67C3A] text-white shadow-xl hover:shadow-2xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5 animate-bounce" />
                    Sending Your Quote...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Zap className="w-5 h-5" />
                    Get Quote Now via Instant SMS
                  </span>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2">We&apos;ll send your SMS quote instantly and follow up with a call during business hours.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}